/**
 * Mating predictor API response and UI types.
 *
 * Response shapes mirror the i6 backend REST API (camelCase JSON):
 * - ProjectedEbv     → i6_Service.Models.Dtos.ProjectedEbvDto
 * - InbreedingResult → i6_Service.Models.Dtos.InbreedingResultDto
 * - MatingPairResult → i6_Service.Models.Dtos.MatingPairResultDto
 * - BreedAverageEbv → i6_Service.Models.Dtos.BreedAverageEbvDto
 * - MatingSelectionCriteria → i6_Service.Models.Dtos.MatingSelectionCriteriaDto
 * - AnimalEbvRecord  → i6_Service.Models.Dtos.AnimalEbvDto (subset + index signature for EBV1–26)
 * - MatingPredictorResponse → i6_Service.Models.Dtos.MatingPredictorResponseDto
 *
 * EBV column config is reused from @/hooks/publicSearch/ebv/ebvColumns (EbvConfigItem, parseEbvConfig).
 */

export type ProjectedEbv = {
  ebvNumber: number;
  value: number | null;
  accuracy: string | null;
  accuracyValue: number | null;
  /** Expected progeny percentile from midparent EBV band lookup (i4 graph algorithm). */
  percentile: number | null;
};

export type InbreedingResult = {
  coefficient: number;
  coefficientPercent: string;
  minimumGenerations: number;
  averageRelatedness: number;
};

export type MatingPairResult = {
  sireId: number;
  damId: number;
  projectedEbvs: ProjectedEbv[];
  inbreeding: InbreedingResult | null;
};

type MatingSelectionCriteria = {
  sireIdents: string[];
  damIdents: string[];
  damCalvingYears: string | null;
  displayBy: string;
};

type BreedAverageEbv = {
  ebvNumber: number;
  value: number | null;
  label: string | null;
};

export type AnimalEbvRecord = {
  animalId: number;
  animalIdent: string | null;
  animalName: string | null;
  sex: string | null;
  dateOfBirth: string | null;
  sireId: number | null;
  sireIdent: string | null;
  sireName: string | null;
  damId: number | null;
  damIdent: string | null;
  damName: string | null;
  [key: string]: unknown;
};

export type MatingPredictorResponse = {
  calculatedAt: string;
  selectionCriteria: MatingSelectionCriteria;
  sires: AnimalEbvRecord[];
  dams: AnimalEbvRecord[];
  results: MatingPairResult[];
  breedAverages: BreedAverageEbv[];
  warnings: string[];
};

/** Mating results grouping: pairs grouped by Sire or by Dam. */
export const MATING_DISPLAY_BY = {
  SIRE: 'Sire',
  DAM: 'Dam',
} as const;

export type MatingDisplayBy =
  (typeof MATING_DISPLAY_BY)[keyof typeof MATING_DISPLAY_BY];

export type MatingSearchFormValues = {
  sireIdents: string;
  damIdents: string;
  damCalvingYears: string;
  maxAnimals: number;
  displayBy: MatingDisplayBy;
};

/** A resolved animal option returned from the quick-search dropdown. */
export type AnimalChipOption = {
  animalId: number;
  animalIdent?: string | null;
  animalName?: string | null;
  /**
   * True when the animal has at least one EBV value (EBV1..EBV26) populated.
   * Drives the BarChart icon shown next to each option/chip in the mating
   * predictor's sire/dam selector: coloured when true, greyed when false.
   * Sourced from the non-persisted computed column AnimalEbv.IsEbvData.
   */
  isEbvData?: boolean;
};

/**
 * A single chip value in the Sire/Dam multi-select.
 * Either a resolved animal (selected from the dropdown) or a raw wildcard
 * string the user typed and committed (e.g. "CBV%").
 */
export type SireDamChipValue = AnimalChipOption | string;

export type PedigreeDisplayState = {
  sireId: number;
  damId: number;
  sireIdent: string;
  damIdent: string;
  /** Display label for chart titles; falls back to {@link sireIdent} when absent. */
  sireName?: string;
  /** Display label for chart titles; falls back to {@link damIdent} when absent. */
  damName?: string;
};
